<script setup>
import Multiselect from '@vueform/multiselect';
import {computed, getCurrentInstance} from "vue";
import DefaultLabel from "@/components/global-themed/inputs/DefaultLabel.vue";

const model = defineModel({
  type: [Array, String, Number],
});

defineProps({
  label: {
    type: [String, null],
    required: false,
    default: null,
  },
  placeholder: {
    type: String,
    required: false,
    default: 'Select'
  },
  mode: {
    type: String,
    required: false,
    default: 'single'
  },
  closeOnSelect: {
    type: Boolean,
    default: false
  },
  options: {
    type: [Array, Object, Function],
    required: true
  },
  validationErrors: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  max: {
    type: Number,
    required: false,
    default: -1
  },
  searchable: {
    type: Boolean,
    required: false,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  groups: {
    type: Boolean,
    default: false,
  },
  canDeselect: {
    type: Boolean,
    default: true,
  },
  caret: {
    type: Boolean,
    required: false,
    default: true,
  },
  noOptionsText: {
    type: String,
    required: false,
    default: 'The list is empty',
  },
  noResultsText: {
    type: String,
    required: false,
    default: 'No results found',
  },
  minChars: {
    type: Number,
    required: false,
    default: 0,
  },
  resolveOnLoad: {
    type: Boolean,
    required: false,
    default: true,
  },
  filterResults: {
    type: Boolean,
    required: false,
    default: true,
  },
  delay: {
    type: Number,
    required: false,
    default: -1,
  },
  classes: {
    type: Object,
    required: false,
    default: () => {},
  },
  canClear: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasError: {
    type: Boolean,
    required: false,
  },
  showOptions: {
    type: Boolean,
    required: false,
    default: true,
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const emit = defineEmits(['searchChange', 'close', 'deselect']);

const {slots} = getCurrentInstance();

const hasIconSlot = computed(() => {
  return !!slots.icon;
});
</script>

<template>
  <div>
    <div class="tw-flex tw-flex-col tw-w-full">
      <DefaultLabel
        v-if="label"
        :for="$attrs.id"
        :label="label"
      />

      <multiselect
        :id="$attrs.id"
        v-model="model"
        :can-clear="canClear"
        :caret="caret"
        :close-on-select="closeOnSelect"
        :max="max"
        :mode="mode"
        :groups="groups"
        :options="options"
        :placeholder="placeholder"
        :searchable="true"
        :disabled="disabled"
        :can-deselect="canDeselect"
        :no-options-text="noOptionsText"
        :no-results-text="noResultsText"
        :min-chars="minChars"
        :resolve-on-load="resolveOnLoad"
        :filter-results="filterResults"
        :delay="delay"
        :showOptions="showOptions"
        :classes="{
          container: `tw-relative tw-mx-auto tw-w-full tw-h-full tw-flex tw-items-center ${Object.keys(validationErrors).length > 0 || hasError ? '!tw-border-danger': 'tw-border-neutral'} tw-justify-end tw-box-border tw-cursor-pointer tw-border tw-border-neutral focus-within:tw-border-primary tw-rounded-lg tw-bg-white tw-text-base tw-leading-snug tw-outline-none tw-select-none`,
          containerDisabled: '!tw-cursor-not-allowed tw-bg-neutral-300',
          wrapper: `tw-flex tw-w-full tw-pr-6 tw-py-3 tw-items-center ${Object.keys(validationErrors).length > 0 || hasError ? '!tw-border-danger': 'tw-border-neutral-900'} ${hasIconSlot ? 'tw-pl-14': 'tw-pl-6'}`,
          singleLabel: `tw-flex tw-items-center tw-h-full tw-absolute tw-left-0 tw-top-0 tw-pointer-events-none tw-bg-transparent tw-leading-snug tw-text-neutral-900 tw-truncate tw-max-w-[calc(100%-30px)] ${hasIconSlot ? 'tw-pl-14': 'tw-pl-6'}`,
          search: 'tw-text-neutral-900 tw-leading-5 tw-text-base tw-inset-0 tw-border-0 tw-outline-none tw-focus:ring-0 tw-appearance-none tw-w-full tw-bg-transparent',
          placeholder: `tw-flex tw-items-center tw-h-full tw-absolute tw-left-0 tw-top-0 tw-pointer-events-none tw-bg-transparent tw-leading-5 tw-text-neutral-400 ${hasIconSlot ? 'tw-pl-14' : 'tw-pl-6'} tw-select-none`,
          option: `hover:tw-bg-primary-100 hover:tw-text-neutral-900 tw-flex tw-items-center tw-justify-start tw-box-border tw-text-left tw-cursor-pointer tw-text-base tw-leading-snug tw-py-2 tw-px-3 tw-rounded`,
          optionDisabled: 'tw-opacity-40 tw-cursor-not-allowed',
          options: 'tw-flex tw-flex-col tw-m-0 tw-gap-2 tw-list-none',
          optionSelected: 'tw-text-white tw-bg-primary',
          dropdown: 'tw-max-h-96 tw-absolute -tw-left-px -tw-right-px -tw-bottom-4 tw-transform tw-translate-y-full tw-border tw-border-primary -tw-mt-px tw-overflow-y-scroll tw-z-50 tw-p-4 tw-bg-white tw-flex tw-flex-col tw-rounded-lg',
          dropdownTop: '-tw-translate-y-full tw-top-px tw-bottom-auto tw-rounded-b-none tw-rounded-t-lg',
          dropdownHidden: 'tw-hidden',
          groupLabel:'tw-inline-flex tw-items-center tw-rounded-xl tw-font-bold tw-text-neutral-900 tw-leading-[1.125rem] tw-uppercase tw-py-2.5 tw-px-4 tw-w-full tw-gap-x-4 tw-justify-between',
          groupLabelPointable: 'tw-pointer-events-none',
          groupLabelPointed: 'tw-bg-primary-100',
          groupLabelSelected: 'tw-bg-primary tw-text-white',
          groupLabelSelectedPointed: 'tw-bg-primary tw-text-white',
          groupOptions: 'tw-m-0 tw-px-4',
          caretOpen: 'tw-pointer-events-auto',
          clear: 'tw-flex',
          tags: 'tw-flex-grow tw-flex-shrink tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-pl-2 tw-min-w-0 tw-rtl:tw-pl-0 tw-rtl:tw-pr-2',
          tag: 'tw-bg-primary-300 tw-flex tw-items-center tw-text-white tw-rounded-lg tw-px-2 tw-py-1',
          ...classes,
        }"
        @search-change="(query) => emit('searchChange', query)"
        @close="(query) => emit('close', query)"
        @deselect="(query) => emit('deselect', query)"
      >
        <template #caret="{ isOpen, handleCaretClick }">
          <svg-vue
            class="tw-w-6 tw-h-6 tw-text-neutral-900 -tw-mr-3 tw-shrink-0 tw-transition-transform tw-duration-300 tw-z-10 tw-ml-auto"
            :class="{
              'tw-rotate-180': isOpen,
              'tw-pointer-events-none' : !isOpen
            }"
            icon="icons/dropdown"
            @click="handleCaretClick"
          />
        </template>

        <template #tag="{ option, handleTagRemove }">
          <div
            class="tw-bg-primary-300 tw-flex tw-items-center tw-text-white tw-rounded-lg tw-px-2 tw-py-1 tw-gap-2"
            :class="{
              'is-disabled': disabled
            }"
          >
            {{ option.label }}

            <span v-if="isLoading">
              <i class="fas fa-spinner fa-spin" />
            </span>

            <span
              v-else
              :class="{
                'tw-pointer-not-allowed tw-pointer-events-none': disabled,
              }"
              @mousedown.prevent.stop="handleTagRemove(option, $event)"
            >
              <svg-vue
                class="tw-w-4 tw-h-4 tw-text-white tw-shrink-0 tw-transition-transform tw-duration-300 tw-z-10 tw-ml-auto"
                icon="icons/cross"
              />
            </span>
          </div>
        </template>

        <template #option="{ option }">
          <div
            v-tooltip="{
              placement: 'bottom',
              theme: 'dark-blue-tooltip',
              content: option.tooltip,
              disabled: !option.tooltip,
            }"
            class="tw-flex tw-items-center tw-justify-between tw-w-full"
          >
            <span>{{ option.label }}</span>

            <span
              v-if="option.disabled"
              class="tw-ml-2 tw-text-xs tw-text-neutral-300"
            >
              <svg-vue
                icon="icons/padlock"
                class="tw-w-6 tw-text-neutral-900"
              />
            </span>
          </div>
        </template>
      </multiselect>
    </div>

    <!-- IMPORTANT! Add tw-relative on input when using the slot -->
    <div class="tw-absolute tw-left-6 tw-top-1/2 tw-transform tw--translate-y-1/2">
      <slot name="icon" />
    </div>

    <small
      v-for="(error, index) in validationErrors"
      :key="index"
      class="tw-text-danger tw-self-baseline tw-absolute tw-mt-1"
    >{{ error }}</small>
  </div>
</template>
